import { createSlice } from '@reduxjs/toolkit';

import { STATUS_STATES } from '../../core/constants';
import { needsDescription } from '../../core/utils/images';
import { setStatus, setView, selectImage, getImages, updateImage, generateDescription } from './actions';

export const GALLERY_FILTERS = {
  all: '',
  needsDescription: 'needs-description',
};

const initialState = {
  status: STATUS_STATES.idle,
  statusDescription: '',
  images: [],
  total: 0,
  hasMore: false,
  // Library-wide count, not the current page — drives the completeness banner.
  undescribedCount: 0,
  selectedImageId: null,
  // imageIds currently having a description generated.
  describingImageIds: [],
  view: {
    search: '',
    filter: GALLERY_FILTERS.all,
    page: 1,
    perPage: 24,
  },
};

/**
 * Patch one image in place and keep `undescribedCount` honest. Shared by the
 * manual edit and the AI generation paths, which differ only in provenance.
 */
const patchImage = (state, image) => {
  const index = state.images.findIndex((existing) => existing.imageId === image.imageId);
  if (index === -1) return;

  const had = needsDescription(state.images[index]);
  const has = needsDescription(image);
  // Keep the banner count in step without refetching the whole library.
  if (had && !has) state.undescribedCount = Math.max(0, state.undescribedCount - 1);
  if (!had && has) state.undescribedCount += 1;

  state.images[index] = image;
};

const galleryStore = createSlice({
  name: 'gallery',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(setStatus, (state, { payload }) => {
      state.status = payload.status;
      state.statusDescription = payload.statusDescription || '';
    });

    builder.addCase(setView, (state, { payload }) => {
      // Any change to the search term or filter resets to the first page,
      // otherwise a narrowed result set can land on a page that no longer exists.
      const resetsPage = payload.search !== undefined || payload.filter !== undefined;
      state.view = { ...state.view, ...payload, ...(resetsPage && payload.page === undefined && { page: 1 }) };
    });

    builder.addCase(selectImage, (state, { payload }) => {
      state.selectedImageId = payload;
    });

    builder.addCase(getImages.pending, (state) => {
      state.status = STATUS_STATES.loading;
      state.statusDescription = '';
    });

    builder.addCase(getImages.fulfilled, (state, { payload }) => {
      state.status = STATUS_STATES.idle;
      state.statusDescription = '';
      // "Load more" appends; a first page (or a changed search/filter, which
      // resets to page 1) replaces. Without this, paging would swap the grid out
      // instead of extending it.
      state.images =
        payload.page > 1
          ? [
              ...state.images.filter(
                (existing) => !payload.images.some((incoming) => incoming.imageId === existing.imageId),
              ),
              ...payload.images,
            ]
          : payload.images;
      state.total = payload.total;
      state.hasMore = payload.hasMore;
      state.undescribedCount = payload.undescribedCount ?? 0;
      state.view = { ...state.view, page: payload.page, perPage: payload.perPage };
    });

    builder.addCase(getImages.rejected, (state, { error }) => {
      state.status = STATUS_STATES.error;
      state.statusDescription = error.message;
    });

    builder.addCase(updateImage.fulfilled, (state, { payload }) => {
      patchImage(state, payload);
      state.status = STATUS_STATES.idle;
    });

    builder.addCase(updateImage.rejected, (state, { error }) => {
      state.status = STATUS_STATES.error;
      state.statusDescription = error.message;
    });

    builder.addCase(generateDescription.pending, (state, { meta }) => {
      // Tracked per image so several post-upload generations can run at once and
      // each tile shows its own state.
      state.describingImageIds.push(meta.arg.imageId);
    });

    builder.addCase(generateDescription.fulfilled, (state, { payload, meta }) => {
      state.describingImageIds = state.describingImageIds.filter((id) => id !== meta.arg.imageId);
      // The server returns the image untouched when it declines to describe it, so
      // this is a no-op patch in that case rather than a special path.
      if (payload?.imageId) patchImage(state, payload);
    });

    builder.addCase(generateDescription.rejected, (state, { meta }) => {
      state.describingImageIds = state.describingImageIds.filter((id) => id !== meta.arg.imageId);
      // Deliberately not surfaced as a page error: a missing description is
      // already visible as "Needs description", and the tenant can retry.
    });
  },
});

export default galleryStore.reducer;
